import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import { Combobox, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Fragment, memo, useEffect, useMemo, useState } from "react";
import { ISelectMenuItemData, ISelectMenuWithSubOptions, ISubOptionObjectType } from "../../interface";

const SelectMenuWithSubOptions = memo(
  ({
    itemsClassName,
    showDropdownWhenNoData,
    noDataDropDownContent,
    listBoxClassName,
    className,
    ...props
  }: ISelectMenuWithSubOptions) => {
    const [selectedOptions, setSelectedOptions] = useState<Array<ISelectMenuItemData>>([]);
    const [selectedSubOptions, setSelectedSubOptions] = useState<ISubOptionObjectType[]>([]);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [query, setQuery] = useState("");

    const selectMenuSelectItems = useMemo(() => {
      return props?.selectItems;
    }, [props?.selectItems]);

    const filteredItems = useMemo(() => {
      return query === ""
        ? selectMenuSelectItems
        : selectMenuSelectItems.filter((item) =>
            item?.title?.toLowerCase()?.replace(/\s+/g, "")?.includes(query?.toLowerCase()?.replace(/\s+/g, "")),
          );
    }, [props?.selectItems]);

    const isValid: boolean = true;

    const handleSelectItems = (items: ISelectMenuItemData[]) => {
      setHasUserInteracted(true);
      setSelectedOptions(items);

      // Get selected parent item IDs
      const parentItemIds = items.map((i) => i.id);
      // console.log(parentItemIds, "parentItemIds");
      const newSubOptions = selectedSubOptions?.filter((sub) => parentItemIds?.includes(sub?.parentId));

      // Retain previously selected sub-options that belong to any currently selected parent
      // const newSubOptions = selectedSubOptions.filter((sub) => items.includes[sub?.parentId]);
      const dataParentIds = [...new Set(selectedSubOptions?.map((item) => item?.parentId))];

      // Find missing parentIds
      const missingParentIds = dataParentIds?.filter((id) => !parentItemIds?.includes(id));

      // console.log("Missing parentId(s):", missingParentIds);

      const filteredData = selectedSubOptions.filter((item) => !missingParentIds?.includes(item?.parentId));

      // console.log(filteredData, "filteredData");

      const uniqueIds = Array.from(
        new Set([...selectedSubOptions, ...(items?.[items?.length - 1]?.subSelectItems?.map((item) => item) || [])]),
      );

      const updatedData = uniqueIds.map((item) => ({
        ...item,
        parentId: item?.parentId || items?.[items?.length - 1]?.id || null,
      }));

      // setSelectedSubOptions(newSubOptions);

      if (missingParentIds?.length > 0) {
        setSelectedSubOptions(filteredData);
      } else {
        setSelectedSubOptions(updatedData);
      }
      // Iterate through selected parents and add their sub-options if they were already selected
      // items.forEach((item) => {
      //   if (item.subSelectItems) {
      //     item.subSelectItems.forEach((subItem) => {
      //       if (
      //         props?.defaultSelectedChildIds.includes(subItem.id) || // Check if it was in default selections
      //         selectedSubOptions.some((so) => so.id === subItem.id) // Keep previously selected sub-options
      //       ) {
      //         if (!newSubOptions.some((so) => so.id === subItem.id)) {
      //           newSubOptions.push({
      //             ...subItem,
      //             parentId: item.id,
      //           });
      //         }
      //       }
      //     });
      //   }
      // });
      items.forEach((item) => {
        if (item.subSelectItems) {
          item.subSelectItems.forEach((subItem) => {
            if (!newSubOptions.some((so) => so.id === subItem.id)) {
              newSubOptions.push({ ...subItem, parentId: item.id });
            }
          });
        }
      });
    };

    const handleSelectSubItems = (
      event: React.MouseEvent | React.ChangeEvent,
      parentId: string,
      subOption: ISelectMenuItemData,
    ) => {
      event.preventDefault();
      event.stopPropagation();
      setHasUserInteracted(true);
      if (selectedSubOptions?.map((so) => so.id).includes(subOption.id)) {
        setSelectedSubOptions((prev) => {
          return prev.filter((prevO) => prevO.id !== subOption.id);
        });
        if (selectedSubOptions?.filter((ids) => ids.parentId === parentId)?.length === 1) {
          setSelectedOptions((prev) => {
            return prev.filter((prevO) => prevO.id !== parentId);
          });
        }
      } else {
        setSelectedSubOptions((prev) => {
          return [
            ...prev,
            {
              ...subOption,
              parentId: parentId,
            },
          ];
        });
      }
    };

    const isSubOptionChecked = (subOption: ISelectMenuItemData) => {
      return selectedSubOptions?.map((so) => so.id).includes(subOption.id);
    };

    useAfterFirstRender(() => {
      if (selectAll) {
        handleSelectItems(filteredItems);
      } else {
        handleSelectItems([]);
      }
    }, [selectAll]);

    useEffect(() => {
      if (hasUserInteracted) return;
      if (!props?.defaultSelectedParentIds?.length || selectedOptions?.length > 0) return;
      const filteredParents = props?.selectItems.filter((i) => props?.defaultSelectedParentIds.includes(i.id));
      if (filteredParents?.length > 0) {
        setSelectedOptions(filteredParents);
      }
    }, [hasUserInteracted, props?.defaultSelectedParentIds, props?.selectItems, selectedOptions?.length]);

    useEffect(() => {
      if (hasUserInteracted) return;
      if (!props?.defaultSelectedChildIds?.length || !props?.defaultSelectedParentIds?.length) return;
      if (selectedSubOptions?.length > 0) return;

      const filteredParents = props?.selectItems.filter((i) => props?.defaultSelectedParentIds.includes(i.id));
      if (filteredParents?.length === 0) return;

      const filteredSubOptions: ISubOptionObjectType[] = [];
      filteredParents.forEach((parent) => {
        (parent?.subSelectItems || []).forEach((subItem) => {
          if (props?.defaultSelectedChildIds.includes(subItem.id)) {
            filteredSubOptions.push({
              ...subItem,
              parentId: parent.id,
            });
          }
        });
      });

      if (filteredSubOptions?.length > 0) {
        setSelectedSubOptions(filteredSubOptions);
      }
    }, [
      hasUserInteracted,
      props?.defaultSelectedChildIds,
      props?.defaultSelectedParentIds,
      props?.selectItems,
      selectedSubOptions?.length,
    ]);

    useAfterFirstRender(() => {
      if (props?.onParentItemSelection) {
        props?.onParentItemSelection(selectedOptions);
      }
      if (props?.onChildItemSelection) {
        props?.onChildItemSelection(selectedSubOptions);
      }
    }, [selectedOptions, selectedSubOptions]);

    const renderDisplay = (items: ISelectMenuItemData[]) => {
      if (!items) return "";
      if (items?.length === 0) {
        return "";
      } else if (items?.length === 1) {
        return items[0]?.title;
      } else {
        let label = items[0]?.title ?? "";
        label = label?.length > 15 ? `${label.substring(0, 15)}..` : label;
        return `${label} , +${items.length - 1}`;
      }
    };

    return (
      <div className={clsx("relative ", props?.containerClassName ?? "")}>
        <Combobox
          disabled={props?.disabled && props?.disabled === true ? true : false}
          value={selectedOptions}
          onChange={(items: ISelectMenuItemData[]) => {
            handleSelectItems(items);
          }}
          multiple
          nullable
        >
          {({ open }) => {
            return (
              <>
                {props?.label && (
                  <span className="absolute -top-4  text-xs left-1 text-gray-500 z-10">{props?.label}</span>
                )}
                <div className="relative  w-full">
                  <div
                    className={clsx(
                      "relative w-full cursor-default overflow-hidden rounded-lg border border-gray-300 bg-white text-left  focus:outline-none focus-visible:ring-1 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm",
                      !isValid && "border-none ",
                      props?.disabled && "bg-gray-100",
                    )}
                  >
                    <Combobox.Button className={"w-full"}>
                      <Combobox.Input
                        ref={null}
                        className={clsx(
                          `w-full text-left text-xs font-medium  rounded-lg py-2 pl-3 pr-10 text-gray-500 leading-5  focus:ring-0 ${className}`,
                          isValid
                            ? "text-gray-500 border-none outline-none font-medium"
                            : "text-red-600  border border-red-500  outline-red-500 font-medium",
                          props?.disabled && props?.disabled === true && "bg-gray-200",
                        )}
                        displayValue={(items: ISelectMenuItemData[]) => {
                          return renderDisplay(items);
                        }}
                        autoComplete="off"
                        placeholder={props?.placeholder ?? "Select Multiple"}
                        onChange={(event) => setQuery(event.target.value)}
                      />
                    </Combobox.Button>
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </Combobox.Button>
                  </div>

                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery("")}
                  >
                    <Combobox.Options
                      as="div"
                      style={{ zIndex: 1 }}
                      className={clsx(
                        "absolute  mt-1 w-full  overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none max-h-80 sm:text-sm",
                        listBoxClassName ? listBoxClassName : "",
                      )}
                    >
                      {props?.withDropDownSearch && props?.withDropDownSearch === true && (
                        <Combobox.Option
                          as="div"
                          value={null}
                          className={"w-full flex justify-start items-center"}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <div className="w-full flex justify-start items-center pl-2">
                            <span>
                              <MagnifyingGlassIcon className="w-3 h-3" />
                            </span>
                            <input
                              onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setQuery(e.target.value);
                              }}
                              className="w-full text-gray-500 border-t-0 border-l-0 border-r-0  outline-none ring-0 border-b text-xs pl-2 pr-4 border-b-gray-200 focus:ring-0 focus:outline-none "
                              type="text"
                              placeholder="Search withing options"
                            />
                          </div>
                        </Combobox.Option>
                      )}
                      {props?.withSelectAll === true && query?.length === 0 && (
                        <Combobox.Option
                          as="div"
                          className={({ selected: _l }) =>
                            clsx(
                              "relative cursor-pointer flex font-medium pr-4 pl-2 text-xs justify-between items-center select-none py-2 text-center ",
                              selectAll
                                ? "bg-gray-100 hover:bg-gray-100 text-gray-800 font-medium "
                                : "text-gray-800 hover:bg-gray-800 hover:text-white",
                              itemsClassName && itemsClassName !== "" ? itemsClassName : "",
                            )
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectAll(!selectAll);
                          }}
                          value={null}
                        >
                          <div className="w-full flex justify-start items-center">
                            <input
                              onChange={() => null}
                              type="checkbox"
                              checked={selectAll}
                              className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                            />
                            <span
                              className={`inline-block pl-2  text-left truncate ${selectAll ? "font-medium " : "font-normal"}`}
                            >
                              {props?.selectAllTitle ? props?.selectAllTitle : "Select All"}
                            </span>
                          </div>
                        </Combobox.Option>
                      )}

                      {filteredItems?.length === 0
                        ? showDropdownWhenNoData &&
                          showDropdownWhenNoData === true && (
                            <div className="absolute border border-gray-200 shadow-md  rounded-md mt-2 w-full cursor-default select-none py-2 px-4 text-gray-700">
                              {noDataDropDownContent ? (
                                noDataDropDownContent
                              ) : (
                                <p className="text-gray-500 text-sm text-center">No Data ...</p>
                              )}
                            </div>
                          )
                        : filteredItems?.length > 0 &&
                          filteredItems?.map((item: ISelectMenuItemData) => (
                            <Combobox.Option
                              as="div"
                              ref={null}
                              key={item.id}
                              className={({ selected: _l }) =>
                                clsx(
                                  "relative cursor-pointer flex font-medium pr-4 pl-2 text-xs justify-between items-center select-none py-2 text-center ",
                                  selectedOptions?.find((so) => so?.id === item.id)
                                    ? "bg-gray-100 hover:bg-gray-100 text-gray-800 font-medium "
                                    : "text-gray-800 hover:bg-gray-800 hover:text-white",
                                  itemsClassName && itemsClassName !== "" ? itemsClassName : "",
                                )
                              }
                              value={item}
                            >
                              {({ selected, active: _isActive }) => (
                                <div className="w-full">
                                  <div className="w-full flex justify-start items-center">
                                    <input
                                      onChange={() => null}
                                      type="checkbox"
                                      checked={selectedOptions?.find((so) => so?.id === item?.id) ? true : false}
                                      className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                                    />
                                    <span
                                      className={`inline-block pl-2  text-left truncate ${
                                        selectedOptions?.find((so) => so?.id === item?.id)
                                          ? "font-medium "
                                          : "font-normal"
                                      }`}
                                    >
                                      {item.title}
                                    </span>
                                  </div>{" "}
                                  <div
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                  >
                                    <ul className="mt-2 w-full">
                                      {selected && (
                                        <li className="text-left text-[10px] text-gray-400">Sub Services</li>
                                      )}
                                      {selected &&
                                        item?.withSubItems &&
                                        item?.subSelectItems &&
                                        item?.subSelectItems?.length > 0 &&
                                        item?.subSelectItems?.map((subItem) => {
                                          return (
                                            <button
                                              className={
                                                "pl-5 py-3 hover:shadow-sm hover:bg-blue-200 rounded-md  w-full flex justify-start items-center"
                                              }
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleSelectSubItems(e, item.id, subItem);
                                              }}
                                              key={subItem?.id}
                                            >
                                              <input
                                                type="checkbox"
                                                onChange={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handleSelectSubItems(e, item.id, subItem);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                checked={isSubOptionChecked(subItem)}
                                                className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-black"
                                              />
                                              <span
                                                className={`inline-block pl-2  text-left truncate ${
                                                  isSubOptionChecked(subItem) ? "font-medium " : "font-normal"
                                                }`}
                                              >
                                                {subItem.title}
                                              </span>
                                            </button>
                                          );
                                        })}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </Combobox.Option>
                          ))}
                    </Combobox.Options>
                  </Transition>
                </div>
              </>
            );
          }}
        </Combobox>
      </div>
    );
  },
);

export default SelectMenuWithSubOptions;
